import { CircularProgress, CssBaseline, ThemeProvider } from '@mui/material';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { createTheme } from '@mui/material/styles';
import {
  Routes,
  Route,
  BrowserRouter,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { routes as appRoutes } from './routes/app';
import { routes as loginRoutes } from './routes/logins';
import MiniDrawer from './components/Drawer/MiniDrawer';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import SessionExpiredDialog from './components/SessionExpiredDialog/SessionExpiredDialog';
import { Provider, useSelector } from 'react-redux';
import { RootState, store } from './store/store';
import NiceModal from '@ebay/nice-modal-react';
import { SnackbarProvider } from 'notistack';
import React, { Suspense, useEffect } from 'react';
import { preloadCriticalRoutes } from './utils/routePreloader';
import { PostHogProvider } from 'posthog-js/react';
import { posthogOptions } from './utils/analytics';
import { BackButtonProvider } from './contexts/BackButtonContext';
import { RevaProvider } from './contexts/RevaContext';
import useVersionCheck from './hooks/useVersionCheck';
import { getStorage } from './utils/localOrSessionStorage';
import PwaPrompts from './components/Pwa/PwaPrompts';
import useAppBadge from './hooks/useAppBadge';

/**
 * Mounts the PWA app badge sync (#310) once, alongside `PwaPrompts`. It reads
 * redux state directly rather than `useAuth()`/`AuthContext` because it lives
 * outside `AuthProvider` in the tree below, same as `PwaPrompts`.
 */
const AppBadgeSync = () => {
  useAppBadge();
  return null;
};

const newShade = (hexColor: string, magnitude: number) => {
  hexColor = hexColor.replace('#', '');
  if (hexColor.length === 6) {
    const decimalColor = parseInt(hexColor, 16);
    let r = (decimalColor >> 16) + magnitude;
    r > 255 && (r = 255);
    r < 0 && (r = 0);
    let g = (decimalColor & 0x0000ff) + magnitude;
    g > 255 && (g = 255);
    g < 0 && (g = 0);
    let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
    b > 255 && (b = 255);
    b < 0 && (b = 0);
    return `#${(g | (b << 8) | (r << 16)).toString(16)}`;
  } else {
    return hexColor;
  }
};

const ProtectedRoute = ({
  sellerOnly = true,
  adminOnly = false,
  children,
}: {
  sellerOnly?: boolean;
  adminOnly?: boolean;
  children: JSX.Element;
}) => {
  const { isAuthenticated } = useAuth();
  const loading = useSelector((state: RootState) => state.auth.loading);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const location = useLocation();

  if (loading) {
    return <CircularProgress />;
  }

  if (!isAuthenticated) {
    // Redirect them to the login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that location after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin routes were previously hidden from the sidebar but still reachable by
  // typing the URL, leaving the API as the only thing standing between a signed-in
  // customer and an admin surface. Send anyone who is not an admin home instead.
  if (adminOnly && currentUser?.type !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }

  if (sellerOnly && currentUser?.type === 'PBUYER') {
    return <Navigate to="/login" replace />;
  }

  return children;
};

export function App() {
  const { palette } = createTheme();
  useVersionCheck();

  // Add token handling logic
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get('jwtToken');

    if (accessToken) {
      getStorage().setItem('jwtToken', accessToken);
      // Clean up the URL by removing the access_token parameter
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Preload critical routes after app initialization
  useEffect(() => {
    // Wait a bit for the initial app to fully load
    const timer = setTimeout(() => {
      preloadCriticalRoutes();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const theme = createTheme({
    components: {
      // todo: remove once the issue is addressed: https://github.com/mui/material-ui/issues/31185
      MuiDialogContent: {
        styleOverrides: { root: { paddingTop: '4px !important' } },
      },
    },
    typography: {
      fontFamily: 'Plus Jakarta Sans',
      fontSize: 16,
      button: {
        textTransform: 'none',
      },
    },
    palette: {
      primary: {
        light: '#63b8ff',
        main: '#279AF1',
        dark: '#005db0',
        contrastText: '#fff',
      },
      secondary: {
        main: '#2B4570',
        light: newShade('#2B4570', 25),
        dark: newShade('#2B4570', -25),
        contrastText: '#000',
      },
      success: {
        main: '#27ae60',
        contrastText: '#fff',
      },
      highlight: palette.augmentColor({ color: { main: '#f39c12' } }),
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        <PostHogProvider
          apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
          options={posthogOptions}
        >
          <BrowserRouter>
            <CssBaseline />
            {/* <GlobalStyles /> */}
            <BackButtonProvider>
              <SnackbarProvider
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              >
                <AuthProvider>
                  <NiceModal.Provider>
                    <RevaProvider>
                      <ErrorBoundary>
                        <Suspense
                          fallback={
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100vh',
                                backgroundColor: '#f6f9fb',
                                gap: '16px',
                              }}
                            >
                              <CircularProgress size={48} />
                            </div>
                          }
                        >
                          <Routes>
                            <Route>
                              {loginRoutes.map((route) => (
                                <Route
                                  key={route.key}
                                  path={route.path}
                                  element={<route.component />}
                                />
                              ))}
                            </Route>
                            <Route element={<MiniDrawer />}>
                              {appRoutes.map((route) => (
                                <Route
                                  key={route.key}
                                  path={route.path}
                                  element={
                                    route.protected ? (
                                      <ProtectedRoute
                                        sellerOnly={route.sellerOnly}
                                        adminOnly={route.adminOnly}
                                      >
                                        <route.component />
                                      </ProtectedRoute>
                                    ) : (
                                      <route.component />
                                    )
                                  }
                                />
                              ))}
                            </Route>
                            <Route
                              path="/"
                              element={<Navigate to="/login" replace />}
                            />
                            <Route
                              path="*"
                              element={<Navigate to="/404" replace />}
                            />
                          </Routes>
                        </Suspense>
                      </ErrorBoundary>
                      <SessionExpiredDialog />
                    </RevaProvider>
                  </NiceModal.Provider>
                </AuthProvider>
              </SnackbarProvider>
            </BackButtonProvider>
            <PwaPrompts />
            <AppBadgeSync />
            {/* <Footer /> */}
          </BrowserRouter>
        </PostHogProvider>
      </Provider>
    </ThemeProvider>
  );
}

export function WrappedApp() {
  return <App />;
}
